import { memo } from 'react';

import { ChevronRightIcon, MailIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import IntegrationStatus from '@/components/pages/Integrations/IntegrationStatus';
import { focusVisibleRing } from '@/components/ui/constants';
import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';
import { EmailIntegration } from '@/shared/types/integration';
import { timeFromNow } from '@/util/dateUtils';

interface EmailItemProps {
  integration: EmailIntegration;
}

/**
 * EmailItem - Individual email integration item component
 *
 * Displays a single email integration with its status, icon, and navigation.
 * Shows email address with display name and status indicator.
 *
 * @param {EmailItemProps} props - Component props
 * @param {Integration} props.integration - The email integration data
 * @returns {JSX.Element} The email item component
 */
const EmailItem = memo(({ integration }: EmailItemProps) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);

  // Extract email info from integration config based on emailIntegrationConfig schema
  const emailAddress = integration.config.from_email || 'Unknown email';
  const displayName = integration.config.from_name || 'Email Integration';

  return (
    <Link
      to={`/${slug}/environments/${deskId}/integrations/${integration.integration_id}`}
      className={cn(
        'group flex w-full cursor-pointer items-center gap-4 rounded-lg border border-border bg-background px-4 py-4 transition-colors hover:bg-muted/50',
        focusVisibleRing
      )}
    >
      {/* Icon */}
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border-[0.5px] border-border bg-subtle p-2">
        <div className="flex items-center justify-center rounded bg-background p-1 shadow-sm">
          <MailIcon className="h-6 w-6 text-muted-foreground" />
        </div>
      </div>

      {/* Name and additional info */}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <div className="flex items-center gap-3">
          <InfoTooltip content={`${displayName} · (${emailAddress})`} size="large" side="top">
            <span className="truncate text-base leading-none font-medium text-foreground">
              {displayName} · ({emailAddress})
            </span>
          </InfoTooltip>
          <IntegrationStatus integration={integration} />
        </div>
        <span className="truncate text-sm leading-5 font-normal text-muted-foreground">
          {integration.last_session_at ? (
            <TimestampTooltip timestamp={integration.last_session_at}>
              {`${t('connections.last_conversation')}: ${timeFromNow(integration.last_session_at)}`}
            </TimestampTooltip>
          ) : (
            t('connections.no_conversations_yet')
          )}
        </span>
      </div>

      {/* Chevron */}
      <ChevronRightIcon
        size={24}
        className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
      />
    </Link>
  );
});

EmailItem.displayName = 'EmailItem';

export default EmailItem;
